import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export interface ExistingVideo {
  id: string;
  name: string | null;
  file_name: string;
  duration: number | null;
  has_transcript: boolean;
  status: string;
}

interface ExistingVideoPickerProps {
  projectId: string;
  selectedId: string | null;
  onSelect: (video: ExistingVideo) => void;
  /** Excludes a video from the list — e.g. the one currently open in this workspace. */
  excludeId?: string | null;
  /** Only list videos that already have a transcript. */
  requireTranscript?: boolean;
}

const formatDuration = (seconds: number | null): string => {
  if (!seconds || seconds <= 0) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const ExistingVideoPicker: React.FC<ExistingVideoPickerProps> = ({ projectId, selectedId, onSelect, excludeId, requireTranscript }) => {
  const [videos, setVideos] = useState<ExistingVideo[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/projects/${projectId}/videos`, { credentials: 'include' })
      .then(res => (res.ok ? res.json() : Promise.reject(new Error('Failed to load videos'))))
      .then((data: ExistingVideo[]) => { if (!cancelled) setVideos(data); })
      .catch(() => { if (!cancelled) setError('Could not load your videos.'); });
    return () => { cancelled = true; };
  }, [projectId]);

  const options = (videos || []).filter(v => v.id !== excludeId && (!requireTranscript || v.has_transcript));

  if (error) return <div className="cm-field-help">{error}</div>;
  if (!videos) return <div className="cm-field-help">Loading your videos…</div>;
  if (!options.length) return <div className="cm-field-help">No previously uploaded videos in this project yet.</div>;

  return (
    <div className="opt-row-group evp-list">
      {options.map(video => {
        const active = video.id === selectedId;
        const duration = formatDuration(video.duration);
        return (
          <label key={video.id} className={`opt-row ${active ? 'opt-row--active' : ''}`}>
            <input
              type="radio"
              name="existing-video-picker"
              checked={active}
              onChange={() => onSelect(video)}
            />
            <span className="opt-row-body">
              <span className="opt-row-title">
                {video.name || video.file_name}
                {video.has_transcript && <span className="opt-row-tag">has transcript</span>}
              </span>
              {duration && <span className="opt-row-desc">{duration}</span>}
            </span>
          </label>
        );
      })}
    </div>
  );
};

export default ExistingVideoPicker;
